import { clone } from '@helpers/Global';
import LayoutHelper from '@helpers/Layouts';
import AssetHelper from '@helpers/Asset';

import { DEVICE, SIZE } from '@master/constants';

let AssetHelperLayouts = {};

AssetHelperLayouts.getSize = (creative, slot, options = {}) => {
  const layout = creative.layout;
  const asset_object = slot != null ? clone(slot) : {};
  return AssetHelper.getSizeDimensions(creative, options, layout, asset_object);
};

AssetHelperLayouts.getLayoutSlots = layout_id => {
  const _modules = LayoutHelper.getUiGroups(layout_id);

  const get = input => {
    const excludes = ['extra.effects_widgets'];
    let result = [];

    for (const item of Object.values(input)) {
      if (excludes.includes(item.ui_group_key)) continue;

      // if theres modules, find assets from there
      let modules = null;
      if (item.modules && Object.keys(item.modules).length > 0) {
        modules = get(item.modules);
      }

      // if theres slots, find assets from there
      let slots = null;
      if (item.slots && Object.keys(item.slots).length > 0) {
        slots = get(item.slots);
      }

      // if this item is asset type, push it to the array
      if (item.slot_type === 'asset') {
        result.push({
          id: item._fullpath,
          type: item.type,
          required: item.required,
          position: item.position,
          width: item.width,
          height: item.height,
          extensions: item.extensions,
          order: item.order,
        });
      }

      // if this item has modules, push them to the output
      if (modules != null) {
        modules.forEach(module => {
          // assign parent props if they exist
          module.type = item.type || module.type;
          module.required = item.required || module.required;
          module.position = item.position || module.position;
          module.width = item.width || module.width;
          module.height = item.height || module.height;
          module.extensions = item.extensions || module.extensions;
          module.order = item.order > module.order ? item.order : module.order;
          module._slot_id = item.slot_id || null;
          result.push(module);
        });
      }

      // if this item has slots, push them to the output
      if (slots != null) {
        slots.forEach(slot => {
          // assign parent props if they exist
          slot.type = item.type || slot.type;
          slot.required = item.required || slot.required;
          slot.position = item.position || slot.position;
          slot.width = item.width || slot.width;
          slot.height = item.height || slot.height;
          slot.height = item.extensions || slot.extensions;
          slot.order = item.order > slot.order ? item.order : slot.order;
          slot._slot_id = slot.id;
          result.push(slot);
        });
      }
    }

    return result;
  };

  return get(_modules);
};

/**
 * Helper function that will create background images used by cropper
 * Will create 3 different layers of images
 * Background - the most bottom layer for cropper, can be transparent if empty, dark overlay will cover the images
 * Overlay    - the most top layer for cropper,    can be transparent if emtpy, dark overlay will not cover the images
 * Assets     - the common assets layer, will be between backround and overlay, will be gray asset color of no assets found, dark overlay will cover the image
 */
AssetHelperLayouts.createCropperBackground = async (slot_vnode, callback) => {
  const creative = slot_vnode.creative;
  const layout_asset = slot_vnode.asset;

  const active_asset_id = slot_vnode.$vnode.key;

  const active_is_overlay = AssetHelper.isOverlay(layout_asset);
  const active_is_background = AssetHelper.isBackground(layout_asset);

  const slots = AssetHelperLayouts.getLayoutSlots(creative.layout_id);

  const active_asset_slot = slots.find(_slot => _slot.id === active_asset_id);

  // map out object, to reduce time finding slots by id from array
  let slots_map = {};
  for (const slot of slots) {
    slots_map[slot.id] = slot;
  }

  const { layers, canvas_width, canvas_height } =
    AssetHelper.createCropperBackgroundLayers(creative);

  let valid_slot_asset_ids = slots
    .filter(slot => {
      return (
        !AssetHelper.isJson(slot.extensions.join(',')) &&
        !AssetHelper.isCsv(slot.extensions.join(',')) &&
        !slot_vnode.singleLayerAsset(slot.id)
      );
    })
    .map(slot => slot.id);

  let asset_ids = valid_slot_asset_ids;

  // reorder assets, making overlays the last ones that are drawn, where thus background assets the first ones, everything else in the middle in asset order
  asset_ids = asset_ids.sort((a, b) => {
    const slot_a = slots_map[a];
    const slot_b = slots_map[b];
    if (slot_a.order > slot_b.order) {
      return 1;
    } else if (slot_a.order < slot_b.order) {
      return -1;
    }
    return 0;
  });

  const valid_additional_asset_ids = Object.keys(
    creative.additional_assets,
  ).filter(asset_id => {
    // custom assets/additionals are hidden from cropper
    return !AssetHelper.isHiddenInCropper(asset_id);
  });

  asset_ids = asset_ids.concat(valid_additional_asset_ids);

  let drawings = [];
  const loaded = () => {
    AssetHelper.drawCropperBackgroundLayers(
      layers,
      drawings,
      callback,
      creative,
    );
  };

  // track where non overlay assets are drawn, since we only draw the 1st overlay asset if theres multiple to the same location
  let asset_layers = [];

  // push first active layer coorinates to the layrs list, so we wont draw anything to the same coorinates
  // except if its overlay, then we want to have the first required asset
  if (layout_asset != null && !active_is_background && !active_is_overlay) {
    asset_layers.push({
      tx: layout_asset.x || 0,
      ty: layout_asset.y || 0,
    });
  }

  let assets_to_draw = {};

  for (const asset_id of asset_ids) {
    if (asset_id === active_asset_id) continue;

    const slot = slots.find(_slot => _slot.id === asset_id);
    // one slot can only hold one asset module

    if (
      active_asset_slot == null ||
      slot == null ||
      slot._slot_id === active_asset_slot._slot_id
    )
      continue;

    let asset = creative.assets[asset_id];

    // dont draw uploaded assets that hasve not been cropped yet
    if (asset != null && asset.output_scale == null) {
      continue;
    }

    // identifier key for which background layer group it belongs
    let layer_type = 'assets';

    // non uploaded assets are replaced with gray rects
    if (asset == null) {
      if (
        slot != null &&
        (!slot.required || AssetHelper.isFloatingObject(slot))
      ) {
        // dont create fake gray assets on non required assets
        continue;
      }

      const sizes = AssetHelperLayouts.getSize(creative, slot);

      asset = {
        asset_id,
        image_uri: null,
        width: sizes.dimensions.width,
        height: sizes.dimensions.height,
        user_scale: 1,
        output_scale: 1,
      };
    }

    const image_uri = asset.image_uri;

    let width = asset.width;
    let height = asset.height;

    let x = 0;
    let y = 0;

    if (AssetHelper.isAdditional(asset_id) || AssetHelper.isOverlay(slot)) {
      if (image_uri == null || image_uri === '') {
        // dont draw overlay assets with no image_uri
        continue;
      }

      const dimensions = AssetHelper.getOverlayPosition(
        creative,
        asset_id,
        { width: canvas_width, height: canvas_height },
        slot_vnode.overlays_inside_placement,
      );
      x = dimensions.x;
      y = dimensions.y;
      width = dimensions.width;
      height = dimensions.height;
      layer_type = 'overlay';
      assets_to_draw[slot._slot_id] = {
        image_uri,
        x,
        y,
        width,
        height,
        layer_type,
      };
    } else {
      // legacy fix before cropper normalize
      if (asset.output_scale == null) {
        if (creative.device === DEVICE.MOBILE) {
          const ratio = SIZE.FW.INTERSTITIAL.WIDTH / creative.width;
          asset.output_scale = ratio;
        } else if (creative.device === DEVICE.DESKTOP) {
          asset.output_scale = 2;
        } else {
          asset.output_scale = 1;
        }
      }

      width /= asset.output_scale;
      height /= asset.output_scale;

      let tx = layout_asset.x || 0;
      let ty = layout_asset.y || 0;
      x = tx;
      y = ty;

      // skip this part for background assets
      if (!AssetHelper.isBackground(layout_asset)) {
        // dont include same position assets
        let includes = false;
        for (const layer of asset_layers) {
          // if module is at same position and in different slot, dont include it
          if (
            layer.tx === tx &&
            layer.ty === ty &&
            layer.slot_id !== slot._slot_id
          ) {
            includes = true;
            break;
          }
        }

        // if same position is already in drawing order, dont add it to the draw list
        if (includes) continue;

        // special case for asset x and y, if they are -1 or 1, they are positioned on top or bottom (like overlays)
        if (x === 1) {
          x = canvas_width / 2 - width / 2;
        } else if (x === -1) {
          x = -canvas_width / 2 + width / 2;
        }
        if (y === 1) {
          y = canvas_height / 2 - height / 2;
        } else if (y === -1) {
          y = -canvas_height / 2 + height / 2;
        }

        // save drawing x and y as well layout x and y position to prevent drawing same location assets twice
        // also save slot_id, because one slot can have image or video, if one exists it will overwrite the other
        asset_layers.push({ x, y, tx, ty, slot_id: slot._slot_id });
      } else {
        layer_type = 'bg';
      }

      if (image_uri != null || assets_to_draw[slot._slot_id] == null) {
        // add or overwrite each slot asset to be drawn (image x video)
        assets_to_draw[slot._slot_id] = {
          image_uri,
          x,
          y,
          width,
          height,
          layer_type,
        };
      }
    }
  }

  // if no drawings are made, fire loaded instantly (case with one asset, the same which is active)
  if (Object.keys(assets_to_draw).length === 0) {
    loaded();
    return;
  }

  for (const slot_id in assets_to_draw) {
    const index = drawings.push(null) - 1;
    const asset = assets_to_draw[slot_id];
    AssetHelper.loadAssetForCropperBackground(asset).then(loaded_asset => {
      drawings[index] = loaded_asset;
      if (!drawings.includes(null)) {
        loaded();
      }
    });
  }
};

export default AssetHelperLayouts;
